/**
 * 通知センター (Notification Centre) — CONCEPT SCREEN, front-end only.
 *
 * The list is served by a local mock today (services/admin/notification-center/mockApi.ts). The backend
 * contract is expected to change, so everything the screen renders is expressed as ONE normalised view
 * model — NotificationItem — and the raw server payload is translated into it in a single adapter
 * (services/admin/notification-center/adapter.ts).
 *
 * That is the whole point of the split: when the API lands, the adapter is the only file that has to know
 * what the API looks like. Columns, filters, badges and bulk actions all read the view model and stay put.
 */

/* ------------------------------------------------------------------------------------------------ tabs */

/**
 * Three inboxes, because the three answer different questions and are read by different people:
 *   MASTER  — did our master data reach 審査?           (data team)
 *   REZEPT  — did this clinic's レセプト reach 審査?     (rezept team)
 *   INBOUND — what did 審査 send back about a レセプト?  (rezept team)
 */
export const NOTIFICATION_TAB = {
  MASTER: 'master',
  REZEPT: 'rezept',
  INBOUND: 'inbound',
} as const;

export type NotificationTab = (typeof NOTIFICATION_TAB)[keyof typeof NOTIFICATION_TAB];

/**
 * The three rezept operations 通知センター groups, mirroring SyncRunNotifier::OPERATION_TITLE.
 *
 * These are the SAME two events Shinsa files on its own 同期通知 tabs (確定取消 / 返戻取消依頼), so the
 * words on the two screens match on purpose — an operator comparing them is reading one vocabulary.
 */
export const NOTIFICATION_OPERATION = {
  UPSERT: 'upsert',
  INVALIDATE: 'invalidate',
  CANCEL_RETURN: 'cancel_return',
} as const;

export type NotificationOperation =
  (typeof NOTIFICATION_OPERATION)[keyof typeof NOTIFICATION_OPERATION];

/** Fallback labels. The server sends its own; these only cover a row written before it did. */
export const NOTIFICATION_OPERATION_LABEL: Record<NotificationOperation, string> = {
  [NOTIFICATION_OPERATION.UPSERT]: 'レセプト同期',
  [NOTIFICATION_OPERATION.INVALIDATE]: '確定取消',
  [NOTIFICATION_OPERATION.CANCEL_RETURN]: '返戻取消依頼',
};

/**
 * Tag colours for 操作. Never the only signal — the word is always printed beside it.
 *
 * The ordinary send is neutral because it is almost every row; the two that undo something are tinted so
 * they stand out of a list of hundreds without having to be read. 返戻取消依頼 keeps the gold this flow
 * already wears elsewhere on the product.
 */
export const NOTIFICATION_OPERATION_COLOR: Record<NotificationOperation, string> = {
  [NOTIFICATION_OPERATION.UPSERT]: 'default',
  [NOTIFICATION_OPERATION.INVALIDATE]: 'orange',
  [NOTIFICATION_OPERATION.CANCEL_RETURN]: 'gold',
};

/**
 * Screen title — used by the page heading AND by the header bell that opens it.
 *
 * One constant so the bell's aria-label and the screen it lands on cannot drift apart; a bell announced as
 * one screen that opens another is a screen-reader bug nobody sees in review.
 */
export const NOTIFICATION_CENTER_TITLE = '通知センター';

export const NOTIFICATION_TAB_LABEL: Record<NotificationTab, string> = {
  [NOTIFICATION_TAB.MASTER]: 'マスタ同期',
  [NOTIFICATION_TAB.REZEPT]: 'レセプト同期',
  [NOTIFICATION_TAB.INBOUND]: '審査システムからの通知',
};

/** One line under the tab bar saying what this inbox is for. Users land here from a bell, not from a spec. */
export const NOTIFICATION_TAB_HINT: Record<NotificationTab, string> = {
  // NAMES THE THREE THAT ACTUALLY SYNC. The old wording said 「治療院・施術者・保険者など」 and 施術者 does
  // not sync at all — it has a constant and a config entry but no dispatcher anywhere (see MASTER_KIND).
  // Promising a category the tab can never show is how a reader concludes the screen is dropping messages.
  [NOTIFICATION_TAB.MASTER]:
    '治療院・保険者のマスタを審査システムへ送信した結果です。失敗したものは再送信できます。',
  // Says 「1行 = 1回の送信」 outright. Without it a reader counts rows and thinks that is how many レセプト
  // were sent — the row is one 確定 press covering a clinic × 提出年月 × 施術区分.
  // Names all THREE operations now on this tab. Saying only 「確定」 while 確定取消 and 返戻取消依頼 rows
  // sit underneath is how a reader concludes the screen is mixing in something it should not show.
  [NOTIFICATION_TAB.REZEPT]:
    '確定・確定取消・返戻取消依頼を審査システムへ送信した結果です（1行 = 1回の操作）。失敗した件数がある場合は再送信できます。',
  // NO mention of failure or re-import: nothing on this tab can fail (a message is only listed because
  // Shinsa delivered it), the 結果 column is gone and there is no 再取込 button. The old sentence promised
  // both and contradicted the screen underneath it.
  // Names the four, and says what they have in common. 審査済/入金済 are applied but not notified, so
  // promising 「入金などの通知」 here would have people waiting for a row that never comes.
  [NOTIFICATION_TAB.INBOUND]:
    '審査システムから届いた返戻・返戻取消・消込解除・一部不支給の通知です。レセプトの状態は自動で更新されています。',
};

/* ----------------------------------------------------------------------------------------------- 種別 */

/**
 * 種別 — 情報 / 成功 / エラー. 👤 Owner's manager, 2026-08-27.
 *
 * REPLACES the earlier 送信状況 (送信中 / 成功 / 失敗), and the change is not just wording. The old three
 * described the state of a SEND; these three describe a NOTIFICATION about a sync RUN, which is what the
 * manager asked the row to be:
 *
 *   info     同期を開始しました（対象 N 件）
 *   success  同期が完了しました  + 同期時間 + 件数
 *   error    同期でエラーが発生しました + 同期時間 + N件中 M件成功 / K件エラー + エラー詳細
 *
 * 情報 covers what 送信中 used to: a run that has started and not yet reported back. The word changed
 * because the row is now raised AT THE START rather than describing something in flight — the spinner still
 * renders while `finishedAt` is null, so nothing is lost on screen.
 */
export type NotificationLevel = 'info' | 'success' | 'error';

export const LEVEL_LABEL: Record<NotificationLevel, string> = {
  info: '情報',
  success: '成功',
  error: 'エラー',
};

/** Column header shared by the two sync tabs. */
export const LEVEL_COLUMN_TITLE = '種別';

/* -------------------------------------------------------------------------------- 受診者 vocabulary */

/**
 * 受診者名 / 受診者番号 — NOT 患者名 / 患者番号.
 *
 * The word is taken from the screen this tab SENDS PEOPLE TO. A 返戻 notification is read and then acted on
 * in 返戻・問合せレセプト登録, and that table heads the same two values 「受診者番号」 and 「受診者名」
 * (ReturnRezeptTable.tsx:95,119); so does the レセプト list beside it (RezeptListTable.tsx:229). Across the
 * whole front end 受診者名 appears 66 times and 受診者番号 58, against 9 for 患者名 — and every one of those
 * nine was either this screen or the 患者 master itself.
 *
 * Named here rather than typed out three times because the column, the search label and the CSV header have
 * to agree: a notification that says 患者名 and a destination screen that says 受診者名 makes a reader stop
 * and wonder whether they are the same person.
 */
export const PATIENT_COLUMN = {
  SERIAL: '受診者番号',
  NAME: '受診者名',
} as const;

/* ----------------------------------------------------------------------------- inbound message kinds */

/**
 * 種別 on the 審査システムからの通知 tab — FOUR kinds. 👤 Owner's decision, 2026-08-27.
 *
 * Shinsa pushes SIX live events; only four of them become a notification here:
 *
 *   ✅ returned           返戻       — nothing was paid, the clinic must fix and resubmit
 *   ✅ cancel_return      返戻取消    — a 返戻 was withdrawn, the rezept goes back to 確定
 *   ✅ payment_cancelled  消込解除    — a settled payment was released, the rezept goes back to 審査済
 *   ✅ (returned, 返戻区分 9)  一部不支給 — part was paid; do NOT resubmit
 *
 *   ❌ reviewed  審査済   — NO NOTIFICATION AT ALL. 👤 Owner: 「nếu show thì quá nhiều」.
 *   ❌ paid      入金済
 *
 *                          RAH still RECEIVES and APPLIES both — the rezept's 審査状況 label moves — they
 *                          simply never become a row here. These two fire for EVERY rezept that completes
 *                          normally, so notifying them would bury the four below in the ordinary case: on
 *                          the local database alone 審査済 covers essentially the whole 40,000-row
 *                          benchmark set, against 9 返戻. A mailbox that fills with good news stops being
 *                          read, and that is exactly how the 返戻 in among them gets missed.
 *
 * 🛑 arrived (到着済) is not on either list: it has had NO production caller since 2026-08-05
 * (RezeptStatusDispatcher.php:313 says so in as many words).
 *
 * WHAT THE FOUR HAVE IN COMMON: every one of them either takes money back or takes a verdict back. That is
 * the whole selection rule, and it is worth keeping in mind if a seventh event ever shows up.
 */
export const INBOUND_KIND = {
  RETURNED: 'returned',
  RETURN_CANCELLED: 'cancel_return',
  PAYMENT_CANCELLED: 'payment_cancelled',
  /**
   * NOT a wire event of its own — it rides in on `returned` with 返戻区分 (RET_KBN) = 9.
   *
   * Listed separately because it is the OPPOSITE INSTRUCTION to the clinic: 返戻 means 「nothing was paid,
   * fix it and submit again」, 一部不支給 means 「part of it WAS paid, do NOT resubmit」. One word for both
   * would tell a clinic to re-claim money it already holds. RAH splits them the same way at the label layer
   * (RezeptStatusHandler.php:2034).
   */
  PARTIAL_UNPAID: 'partial_unpaid',
} as const;

export type InboundKind = (typeof INBOUND_KIND)[keyof typeof INBOUND_KIND];

export const INBOUND_KIND_LABEL: Record<InboundKind, string> = {
  [INBOUND_KIND.RETURNED]: '返戻',
  [INBOUND_KIND.RETURN_CANCELLED]: '返戻取消',
  [INBOUND_KIND.PAYMENT_CANCELLED]: '消込解除',
  [INBOUND_KIND.PARTIAL_UNPAID]: '一部不支給',
};

/**
 * antd Tag colours.
 *
 * 返戻 is the only one that asks the clinic to DO something, so it is the only warm/urgent colour.
 * 一部不支給 deliberately takes purple rather than orange: orange renders within 4/255 of gold on this
 * palette, and two verdicts that look the same colour are worse than an unexpected one — the same decision
 * as in frontend/src/constants/shinsaStatus.ts.
 */
export const INBOUND_KIND_COLOR: Record<InboundKind, string> = {
  [INBOUND_KIND.RETURNED]: 'volcano',
  [INBOUND_KIND.RETURN_CANCELLED]: 'gold',
  [INBOUND_KIND.PAYMENT_CANCELLED]: 'default',
  [INBOUND_KIND.PARTIAL_UNPAID]: 'purple',
};

/* ------------------------------------------------------------------------------- master data kinds */

/**
 * データ種別 on the マスタ同期 tab — WHICH master this notification is about.
 *
 * THREE, and only three, because that is what the pipeline actually pushes. Verified in the RAH repo, not
 * taken from the constant list:
 *
 *   clinic   ✅ SyncMap registers ClinicPayloadBuilder      aggregate_id = {clinic_number}-{sub_number}
 *   insurer  ✅ SyncMap registers InsurerPayloadBuilder     aggregate_id = {insurer_number}-{sub_number}
 *   address  ✅ SyncAddressFileJob — 都道府県/市区町村/町名 go as ONE FILE that replaces the whole table,
 *               not as per-record messages, so one notification covers the batch and there is no code.
 *
 * 🛑 NOT LISTED, deliberately: 施術者 (therapist) and 保険者区分 (insurer-type). Both have a constant in
 * SyncContract and both appear in config('shinsa.sync.aggregates'), but grepping app/ for a dispatch of
 * either returns ZERO hits — no builder in SyncMap, no caller anywhere. A category that can never produce a
 * row would sit at 0 for ever and read as a broken filter, the same trap as 到着済 on the inbound tab.
 *
 * 住所 (address) LEFT THIS LIST ON 2026-09-07 AND CAME BACK ON 2026-09-08 — with the hook it was waiting
 * for. It left because the file push wrote no notification at all, so the filter offered a value that
 * always answered empty while the tab's hint promised it was covered. It is back because
 * SyncAddressFileJob now opens a run row, keeps one sync_states row under 「address-file」 and closes it
 * 成功 / エラー — 👤 owner, 2026-09-08: 「chỉ cần thông báo đơn giản khi sync địa chỉ thành công, thất bại
 * hay đang xử lý thôi · lỗi thì có thể retry」.
 *
 * WHAT ONE ROW MEANS HERE, which is the question the old note said had to be answered first: ONE PUSH, not
 * one record and not one table. 👤 Owner: 「ở 1 action import file csv địa chỉ thì chỉ có thông báo 1 lần
 * duy nhất cho 1 action」. The three address tables travel in one file under one version, so the row is
 * keyed on the aggregate `town` — the name the job already uses for the set.
 */
export const MASTER_KIND = {
  CLINIC: 'clinic',
  INSURER: 'insurer',
  ADDRESS: 'address',
} as const;

export type MasterKind = (typeof MASTER_KIND)[keyof typeof MASTER_KIND];

export const MASTER_KIND_LABEL: Record<MasterKind, string> = {
  [MASTER_KIND.CLINIC]: '治療院',
  [MASTER_KIND.INSURER]: '保険者',
  [MASTER_KIND.ADDRESS]: '住所',
};

export const MASTER_KIND_COLOR: Record<MasterKind, string> = {
  [MASTER_KIND.CLINIC]: 'blue',
  [MASTER_KIND.INSURER]: 'geekblue',
  [MASTER_KIND.ADDRESS]: 'purple',
};

/**
 * 内容 ON マスタ同期 — A SENTENCE, NOT A COUNT. 👤 Owner, 2026-09-02.
 *
 * 「1件中 0件成功、1件エラー」 is meaningless here and was actively wrong on screen: a 治療院 or 保険者 send
 * is ONE record, so the count says nothing a reader did not already know, and the per-send figure it drew
 * came from the audit trail while the row's own 種別 came from the record's live state — the two disagreed
 * and the row showed 「成功」 above 「1件エラー」.
 *
 * WHAT THE SENTENCE HAS TO CARRY instead: which master, whether it was a 新規登録 or an 更新, and where the
 * send stands.
 */
export const MASTER_SUBJECT: Record<MasterKind, string> = {
  [MASTER_KIND.CLINIC]: '治療院マスタ',
  [MASTER_KIND.INSURER]: '保険者マスタ',
  // No 新規登録 / 更新 half ever follows this one: the server sends is_new = null for a whole-table
  // replace, so the sentence reads 「住所マスタを同期しました」 rather than claiming an insert or an update.
  [MASTER_KIND.ADDRESS]: '住所マスタ',
};

/** 新規登録 / 更新 — the half of the subject that only the per-record masters have. */
export const MASTER_CHANGE: Record<'new' | 'update', string> = {
  new: 'の新規登録',
  update: 'の更新',
};

/** Where the send stands, in the same three states the 種別 tag shows. */
export const MASTER_OUTCOME: Record<NotificationLevel, string> = {
  info: 'を同期しています',
  success: 'を同期しました',
  error: 'を同期できませんでした',
};


/* ------------------------------------------------------------------------------------- error detail */

/** One row of the downloadable CSV — a single field the receiving side rejected. */
export interface NotificationErrorRow {
  field: string;
  value: string;
  reason: string;
}

/**
 * Everything behind the エラー詳細 download.
 *
 * It is NOT rendered in the table on purpose: a reviewer scanning 50 rows needs one sentence per row, and
 * whoever is actually fixing the failure wants the whole thing in a file they can sort and share.
 */
export interface NotificationErrorDetail {
  code: string;
  message: string;
  httpStatus: number | null;
  endpoint: string | null;
  occurredAt: string;
  rows: NotificationErrorRow[];
}

/* ------------------------------------------------------------------------------------------- sends */

/**
 * One 送信 — the 確定 press itself, or one 再送信 that followed it.
 *
 * WHY THE COUNTS ARE PER SEND AND NOT CUMULATIVE. The whole point of the stack is to say what each attempt
 * achieved. Read cumulatively every block would end up reporting the same total once the last re-send
 * lands, and the stack would carry no information at all.
 */
export interface NotificationSend {
  correlationId: string;
  /** When this send was dispatched. For a 再送信 this is the moment the button was pressed. */
  startedAt: string | null;
  /** When its last record settled. NULL while any of them is still queued. */
  finishedAt: string | null;
  total: number;
  success: number;
  failure: number;
  /**
   * 対象外 — records the review flow never wanted, counted APART from the three above.
   *
   * A rezept whose 保険者 is on 償還払い is never sent: the patient claims from the insurer themselves. It
   * used to land in `failure` because the job ends in the same state a real breakage does, so a press that
   * did everything right still read 「エラー」. It now leaves `total` as well, which is what lets the
   * sentence add up — 「3件中 3件成功（対象外 1件）」 rather than a fourth record nobody names.
   */
  outOfScope: number;
  running: number;
}

/* --------------------------------------------------------------------------------------- view model */

/** The only shape the screen knows about. See the file header for why. */
/**
 * One rezept inside a 確定取消 / 返戻取消依頼 press.
 *
 * `outcome` is read LIVE from the record's current sync state, not frozen at press time — which is why it
 * sits here rather than inside a send: a rezept repaired by a later re-send reads 成功 on the original line,
 * because that is the true answer to 「did this patient's request reach 審査」.
 */
export interface NotificationRecord {
  /** `rezept-a2` | `rezept-r2` — the screen renders it as the A2/R2 half of the id. */
  aggregate: string;
  aggregateId: string;
  outcome: 'success' | 'failure' | 'running';
  /** 受診者名. Null when the patient row is gone — the line still prints, which is the point. */
  patientName: string | null;
  patientSerial: string | null;
  submitYearMonth: string | null;
}

export interface NotificationItem {
  id: number;
  tab: NotificationTab;
  level: NotificationLevel;
  /** 既読 — a boolean, not a status string, because that is the only question the column asks. */
  read: boolean;
  /**
   * 日時 — when THIS NOTIFICATION was raised. The second column, and one moment, never a span.
   *
   * 👤 Owner's manager, 2026-08-27: the column used to render 「開始 〜 終了」 and that conflated two
   * different things. The sync WINDOW belongs in the 内容 sentence (同期時間：… 〜 …); this column answers
   * 「when did this land in my inbox」, which is what a list is sorted and scanned by.
   */
  occurredAt: string;

  /** 同期時間 start — the run's own window, quoted inside 内容. */
  startedAt: string;
  /**
   * 同期時間 end — NULL while the run is still going, which is exactly the 情報 row's state.
   *
   * The 内容 sentence drops the 同期時間 line entirely when this is null rather than printing 「… 〜 」:
   * an open-ended range reads as a rendering bug, not as 「still running」.
   */
  finishedAt: string | null;

  /** 対象 — what kind of thing this is about (レセプト, レセプト通知 …). Free text. */
  targetLabel: string;
  /** マスタ同期 tab — WHICH master. Null on the other two tabs. */
  masterKind: MasterKind | null;
  /**
   * マスタ同期 — the business code of the record this run is about: 治療院ID **or** 保険者番号.
   *
   * 👤 Owner, 2026-08-27: the マスタ同期 tab shows this pair (code + name) for EVERY kind, not only for
   * clinics. It is kept separate from `clinicNumber` on purpose — those two fields mean a clinic on the
   * other tabs, and putting an insurer number in a field called `clinicNumber` is the kind of lie that
   * survives into the API contract.
   *
   * NULL for the address master, and that is a real answer rather than missing data: the address tables go
   * as one whole-table file, so the run is about a batch and there is no single record to name.
   */
  targetCode: string | null;
  /** The name that belongs to `targetCode` — 治療院名 or 保険者名. Null where the run names no one record. */
  targetName: string | null;
  /** キー — the id used to find the record on either side. The MASTER tab filters on this. */
  targetKey: string;
  /** 内容 — ONE human sentence. Never a stack trace, never JSON. */
  summary: string;

  /**
   * WHAT THE CLINIC PRESSED, on レセプト同期. Three operations share this tab (2026-09-02):
   *
   *   'upsert'        確定 を送信      the ordinary send
   *   'invalidate'    確定取消        the clinic took a rezept back out of 確定
   *   'cancel_return' 返戻取消依頼    the clinic says a 返戻 should not have been
   *
   * THE LABEL IS THE SERVER'S, not a lookup here. It is stored on the row at press time, so a 確定取消
   * that was later re-確定d still reads 確定取消 — the screen is a log of what happened, not a view of
   * what is true now. `operation` is the raw value for branching without parsing Japanese.
   *
   * Null on every other tab: マスタ同期 and 審査システムからの通知 have no 「press」.
   */
  operation: NotificationOperation | null;
  operationLabel: string | null;

  /**
   * ONE ENTRY PER REZEPT — only for 確定取消 and 返戻取消依頼, whose 内容 names patients instead of counting.
   *
   * EMPTY on a 確定 press, and that is not a gap: a press covers a whole clinic-month, no single rezept is
   * its subject, and a 755-entry array for a cell that renders 「755件中…」 would be payload for nothing.
   */
  records: NotificationRecord[];
  /** What `records` would hold uncapped. Anything above `records.length` is stated as 「ほか N件」. */
  recordsTotal: number;

  /**
   * 新規 or 更新 — マスタ同期 only, where a row IS one record.
   *
   * Null on レセプト同期 (a press covers many records, so neither word is true of the row) and on a row
   * written before the field existed, where the sentence falls back to the neutral wording.
   */
  isNew: boolean | null;

  /**
   * サービス区分 — 'A2' (はり・きゅう) or 'R2' (マッサージ). レセプト同期 tab.
   *
   * NEVER 'BOTH'. A clinic can be BOTH, but a レセプト cannot: it lives in either `rezepts` (A2) or
   * `r_rezepts` (R2), and it is pushed under either the `rezept-a2` or the `rezept-r2` aggregate. Neither
   * table carries a service_type column — the aggregate IS the answer, which is why the adapter derives it
   * rather than reading a field.
   */
  serviceType: 'A2' | 'R2' | null;

  /** レセプト / 審査通知 tabs only. */
  clinicNumber: string | null;
  clinicName: string | null;
  /**
   * 受診者名 — 審査システムからの通知 tab.
   *
   * NULL on レセプト同期, and that is an answer rather than missing data: a row there is one SEND covering
   * every patient in a 確定 press, so there is no single name that belongs in the cell.
   */
  patientName: string | null;
  /**
   * 受診者番号 — `patients.serial_number`.
   *
   * PER CLINIC, and it restarts at 1 for every one of them (measured on rah_db 2026-08-27: 42,850 patients,
   * every clinic's numbering begins at 1, the longest running to 11,280). The number ALONE therefore names
   * nobody — it only means something beside the 治療院ID it belongs to, which is why the two columns sit
   * next to each other and never appear apart.
   *
   * NOT 受給者番号 (CONSULT_NO): that is the insurer's number for the patient and a different value.
   *
   * A string, not a number: the column is an identifier to be read and copied, and `0` must never be able to
   * fall out of a truthiness check.
   */
  patientSerial: string | null;
  /** 提出年月, YYYYMM. */
  submitYearMonth: string | null;
  /** 審査システムからの通知 tab only. */
  inboundKind: InboundKind | null;
  /**
   * レセプト同期 tab — this row is a 状態の不一致 notice and the nightly reconcile left a 明細CSV beside it.
   *
   * A FLAG, not the path: the file is fetched through the endpoint, which re-checks the site scope. False on
   * a 確定 press row, and false on a drift row whose file could not be written — so the button is never drawn
   * over something that would answer 404.
   */
  hasDriftCsv: boolean;

  /**
   * 対象件数 — how many records THIS RUN covered. The XXX in 「同期を開始しました（対象 XXX 件）」.
   *
   * 👤 Owner's manager, 2026-08-27: every sync row now carries it, not just レセプト同期. That is the grain
   * change behind this whole revision — a row is ONE RUN, so 「how many」 is always a fair question. It was
   * previously null on マスタ同期, where a row was one clinic or one insurer.
   *
   * NULL on 審査システムからの通知, where a row really is one レセプト for one person and there is no run.
   */
  totalCount: number | null;
  /** How many of `totalCount` landed, and how many did not. Null on the inbound tab, for the same reason. */
  successCount: number | null;
  failureCount: number | null;
  /** 対象外 for the whole press — see NotificationSend.outOfScope. */
  outOfScopeCount: number | null;

  /**
   * ONE ENTRY PER SEND — the 確定 press, then each 再送信 folded into it, oldest first.
   *
   * A re-send does not get a row of its own; it appends a block to this row (owner, 2026-09-01:
   * 「mỗi lần thử lại cứ thêm vào xuống dưới thôi」). Each block carries ITS OWN time and ITS OWN counts, so
   * the stack reads as a history — 「the press got 3 of 4, the re-send got the last one」 — rather than a
   * single line that only ever ends up saying everything succeeded.
   *
   * EMPTY on the tabs that are not about sends. The 内容 cell falls back to the row's own numbers there.
   */
  sends: NotificationSend[];

  /** 試行回数. Still shown on マスタ同期; not a column on レセプト同期 any more (a batch has no one count). */
  attempts: number;
  /**
   * How many times 再送信 has been pressed on THIS press — i.e. `sends.length - 1`.
   *
   * Kept beside `sends` because the two answer different questions: the stack shows WHAT each attempt did,
   * this one answers 「has anyone already pressed this?」 at a glance, which is what two operators sharing an
   * inbox need before they both press it.
   */
  retryCount: number;
  /** Whether 再送信 can help this row. A success row, or a failure nothing can re-push, is false. */
  retryable: boolean;
  errorDetail: NotificationErrorDetail | null;
}

/* ------------------------------------------------------------------------------------------ filters */

/** 既読状態 filter — single select. 'all' is a real value so the control never has an empty state. */
export type ReadFilter = 'all' | 'unread' | 'read';

/**
 * The search conditions. 👤 Owner's manager, 2026-08-27 — five named controls replacing the old mix.
 *
 * What changed, and why each one:
 *   種別        single select -> MULTI. 「失敗と送信中」 is the ordinary morning question and the old
 *               one-of-four could not ask it.
 *   処理時間     date -> DATE AND TIME. A sync run is minutes long; a whole-day bound cannot separate the
 *               07:00 batch from the 19:00 one, which is the usual reason someone opens this screen.
 *   治療院ID     dropdown -> FREE TEXT. The dropdown could only offer clinics already present in the
 *               loaded page, so typing a code that had no row today found nothing and looked broken.
 *   治療院名     NEW, free text. Nobody carries clinic codes in their head.
 *   既読状態     unchanged, single select.
 *
 * DROPPED: キー (partial match on the internal aggregate id). 治療院ID now covers what people actually
 * typed into it, and an internal key is not a search term for this audience.
 *
 * KEPT beyond the five, each on one tab only: 施術区分 on レセプト同期, 受診者 on 審査システムからの通知.
 * Both were separate owner decisions and neither is replaced by the five above.
 */
export interface NotificationFilters {
  /** 処理時間 lower bound, 'YYYY-MM-DDTHH:mm', '' when unset. */
  from: string;
  /** 処理時間 upper bound, same shape. Inclusive — see mockApi. */
  to: string;
  read: ReadFilter;
  /** 種別 — several at once. EMPTY MEANS ALL, so the control never needs an 「すべて」 option of its own. */
  levels: NotificationLevel[];
  /** 治療院ID — free text, partial match. */
  clinicNumber: string;
  /** 治療院名 — free text, partial match. */
  clinicName: string;
  /**
   * レセプト同期 — 施術区分. ONE value: 'A2', 'R2', or '' meaning both.
   *
   * SINGLE, NOT A LIST, because a run is one 確定 press for one service — the two values are mutually
   * exclusive on a row, so 「A2 and R2」 selects exactly what 「neither」 already selects. The control was a
   * multi-select and this field a string[]; the pair could express a state ({A2, R2}) that meant the same
   * as the empty one, which is two ways to say one thing and one more state for every reader of this
   * filter to reason about.
   *
   * Still travels as `service_types: [value]` on the wire — the server takes an array and intersects it
   * against ['A2','R2'], so nothing on that side had to move.
   */
  serviceType: string;
  /**
   * 審査システムからの通知 tab — ONE box matching 受診者名, its カナ, or 受診者番号.
   *
   * One field for all three rather than a box each, because 返戻処理 already searches exactly those three
   * values from a single input (ReturnRezeptRepository.php:56-66) and someone holding a 受診者番号 should
   * not have to work out which box it belongs in.
   *
   * The server side has two normalisations to carry over from that repository, and both matter:
   *   · mb_convert_kana(..., 'C')  — half-width カナ typed as ﾀﾅｶ still finds タナカ
   *   · mb_convert_kana(..., 'aC') — a 受診者番号 typed in full-width digits still finds the record
   * The mock adds one the real screen lacks: it ignores the space inside a 氏名, so 「田中太郎」 finds
   * 「田中　太郎」. Worth carrying over too — that space is invisible to whoever is typing.
   */
  patient: string;
  /**
   * 審査システムからの通知 tab — 種別, several at once. Empty means all, like `levels`.
   *
   * A DIFFERENT FILTER FROM `levels` UNDER THE SAME LABEL. On the two sending tabs 種別 is the level
   * (情報 / 成功 / エラー); here it is which of the four things 審査 sent. The panel offers one or the
   * other, never both, so the reader only ever meets one meaning at a time.
   */
  kinds: InboundKind[];
}

export const EMPTY_FILTERS: NotificationFilters = {
  from: '',
  to: '',
  read: 'all',
  levels: [],
  clinicNumber: '',
  clinicName: '',
  serviceType: '',
  patient: '',
  kinds: [],
};

/** Is anything narrowing the list right now? Decides which empty-state sentence is honest. */
export const hasActiveFilters = (f: NotificationFilters): boolean =>
  !!f.from ||
  !!f.to ||
  f.read !== 'all' ||
  f.levels.length > 0 ||
  !!f.clinicNumber.trim() ||
  !!f.clinicName.trim() ||
  !!f.serviceType ||
  f.kinds.length > 0 ||
  !!f.patient.trim();

/* -------------------------------------------------------------------------------- request / response */

export interface NotificationListQuery extends NotificationFilters {
  tab: NotificationTab;
  page: number;
  perPage: number;
}

/**
 * How many rows a page holds. ONE SIZE, and no control to change it.
 *
 * There was a 表示件数 picker offering 20 / 50 / 100, and a `normalisePerPage` to validate the matching URL
 * parameter. Both are gone: the Hitowa common spec gives every list in the product 20 rows a page and a
 * pager that hides at 20 or fewer (.claude/skills/hitowa-common/SKILL.md § Pagination), so this screen was
 * the only list with a size control on it. Owner, 2026-09-08.
 *
 * Same number as AdminConstants::LIST_DEFAULT_PER_PAGE on the server, which is what the request falls back
 * to when `per_page` is absent — and it is absent now.
 */
export const DEFAULT_PER_PAGE = 20;

export interface NotificationListMeta {
  currentPage: number;
  perPage: number;
  total: number;
  lastPage: number;
}

export interface NotificationListResult {
  data: NotificationItem[];
  meta: NotificationListMeta;
  /** 種別 breakdown of the CURRENT filter, all pages — feeds the summary strip above the table. */
  summary: {
    info: number;
    success: number;
    error: number;
  };
}

/** Unread per tab, every tab present (zeros included) — feeds the tab badges. */
export type NotificationUnreadCounts = Record<NotificationTab, number>;

/** What a bulk button did. Reported back in one toast so the operator knows it landed. */
export interface NotificationBulkResult {
  requested: number;
  affected: number;
  /** Rows the action could not apply to (e.g. 再送信 on a success row). */
  skipped: number;
  /**
   * 再送信 only — rezepts the 500-per-press cap left for a second press.
   *
   * Optional because markRead never sets it, and because a server older than 2026-09-07 does not send it at
   * all; a missing value has to read as 「nothing left over」 rather than as NaN in a sentence about money.
   */
  remaining?: number;
}
